//C:\Users\Anish\xpr_survey\XPRSuvey-App\src\Components\Survey\Modal\PublishResponderModel.jsx
import React, { useEffect, useState } from "react";
import {
    X,
    Upload,
    Download,
    FileSpreadsheet,
    UserCog,
    AlertCircle,
    CheckCircle2,
    Calendar,
} from "lucide-react";
import sidebarImage from "../../../assests/SidebarImage.png";
import SidebarHeaderImage from "../../../assests/SidebarHeaderImage.png";
import { downloadSampleResponderSheet } from "../../../Utility/DownloadSampleExcel";
import { CreateResponders, CreateSingleResponder } from "../Services/SurveyService";
import { getChannels } from "../Services/MasterDataService";

const EXPERIENCE_TYPE_OPTIONS = ["Experienced", "Fresher"];

const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Scheduler runs every 30 min (:00 and :30). Compute the next run time.
const getNextScheduleRun = () => {
    const now = new Date();
    const next = new Date(now);
    next.setSeconds(0, 0);

    if (next.getMinutes() < 30) {
        next.setMinutes(30);
    } else {
        next.setMinutes(0);
        next.setHours(next.getHours() + 1);
    }
    return next;
};

// "in 12 min" style countdown label
const formatCountdown = (targetDate) => {
    const diffMs = targetDate - new Date();
    if (diffMs <= 0) return "running now";
    const totalSeconds = Math.floor(diffMs / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return mins > 0 ? `in ${mins} min` : `in ${secs}s`;
};

const PublishResponderModel = ({ isOpen, survey, onClose, onSuccess }) => {
    const [file, setFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [activeTab, setActiveTab] = useState("bulk");
    const [singleResponder, setSingleResponder] = useState({
        responderEmail: "",
        phoneNumber: "",
        responderName: "",
        channelID: "",
        experienceType: "",
        referenceCode: "",
        location: "",
        gender: ""
    });
    const [channels, setChannels] = useState([]);
    const [loadingChannels, setLoadingChannels] = useState(false);
    const [deadline, setDeadline] = useState("");
    const [nextRunTime, setNextRunTime] = useState(getNextScheduleRun());

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const droppedFiles = e.dataTransfer.files;
        if (droppedFiles && droppedFiles.length > 0) {
            const droppedFile = droppedFiles[0];

            const allowedExtensions = /(\.xlsx|\.xls|\.csv)$/i;
            if (!allowedExtensions.exec(droppedFile.name)) {
                setErrors((prev) => ({
                    ...prev,
                    file: "Invalid file type. Please upload .xlsx, .xls, or .csv",
                }));
                return;
            }

            setFile(droppedFile);
            if (errors.file) setErrors((prev) => ({ ...prev, file: "" }));
        }
    };

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
            setFile(null);
            setErrors({});
            setActiveTab("bulk");
            setDeadline("");
            setSingleResponder({
                responderEmail: "",
                phoneNumber: "",
                responderName: "",
                location: "",
                gender: "",
                channelID: "",
                experienceType: "",
                referenceCode: ""

            });
        } else {
            document.body.style.overflow = "auto";
        }

        return () => {
            document.body.style.overflow = "auto";
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const fetchChannels = async () => {
            try {
                setLoadingChannels(true);
                const response = await getChannels();
                if (response.data.status?.toLowerCase() === "success") {
                    setChannels(response.data.channels || []);
                } else {
                    // eslint-disable-next-line no-undef
                    CustomToast("fail", response.data.message || "Unable to load channels.");
                }
            } catch (error) {
                // eslint-disable-next-line no-undef
                CustomToast("fail", error.response?.data?.message || "Unable to load channels.");
            } finally {
                setLoadingChannels(false);
            }
        };

        fetchChannels();
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const tick = () => {
            const next = getNextScheduleRun();
            // If we've passed the current target (scheduler just fired), recompute
            setNextRunTime((prev) => (!prev || next.getTime() !== prev.getTime() && new Date() >= prev ? next : prev));
        };

        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [isOpen]);


    if (!survey) return null;

    const handleDownloadSample = () => {
        downloadSampleResponderSheet(channels);
    };

    const handleSubmit = async () => {
        const nextErrors = {};

        if (!deadline) {
            nextErrors.deadline = "Deadline is required.";
        } else if (new Date(deadline) <= new Date()) {
            nextErrors.deadline = "Deadline must be in the future.";
        }

        if (activeTab === "bulk") {
            if (!file) {
                nextErrors.file = "Please upload a responder sheet to continue.";
            }

            if (Object.keys(nextErrors).length > 0) {
                setErrors(nextErrors);
                return;
            }

            try {
                setSubmitting(true);
                const response = await CreateResponders(
                    survey.uniqueKey,
                    file,
                    deadline
                );
                if (response.data.status?.toLowerCase() === "success") {
                    CustomToast("success", "Responders saved successfully.");
                    await onSuccess?.();
                    onClose();
                } else {
                    CustomToast("fail", response.data.message || "Failed to upload responders.");
                }
            } catch (error) {
                CustomToast("fail", error.response?.data?.message || error.message || "Unable to save responder settings.");
            } finally {
                setSubmitting(false);
            }
        } else {
            if (!singleResponder.responderName) nextErrors.responderName = "Required";
            if (!singleResponder.responderEmail) nextErrors.responderEmail = "Required";
            if (!singleResponder.channelID) nextErrors.channelID = "Required";
            if (!singleResponder.experienceType) nextErrors.experienceType = "Required";
            if (!singleResponder.location) nextErrors.location = "Required";
            if (!singleResponder.gender) nextErrors.gender = "Required";


            if (Object.keys(nextErrors).length > 0) {
                setErrors(nextErrors);
                return;
            }

            try {
                setSubmitting(true);
                const payload = {
                    AssessmentUID: survey.uniqueKey,
                    Email: singleResponder.responderEmail,
                    PhoneNumber: singleResponder.phoneNumber,
                    ResponderName: singleResponder.responderName,
                    Location: singleResponder.location,
                    Gender: singleResponder.gender,
                    ChannelID: Number(singleResponder.channelID),
                    ExperienceType: singleResponder.experienceType,
                    ReferenceID: singleResponder.referenceCode,
                    Deadline: deadline
                };
                const response = await CreateSingleResponder(payload);

                if (response.data.status?.toLowerCase() === "success") {
                    // eslint-disable-next-line no-undef
                    CustomToast("success", "Responder saved successfully.");
                    await onSuccess?.();
                    onClose();
                } else {
                    // eslint-disable-next-line no-undef
                    CustomToast("fail", response.data.message || "Failed to add responder.");
                }
            } catch (error) {
                // eslint-disable-next-line no-undef
                CustomToast("fail", error.response?.data?.message || error.message || "Unable to save responder.");
            } finally {
                setSubmitting(false);
            }
        }
    };

    return (
        <>
            <div
                onClick={onClose}
                className={`fixed inset-0 bg-slate-900/40 backdrop-blur-[2px] transition-opacity duration-300 z-[9998] ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
                    }`}
            />

            <div
                className={`fixed top-0 right-0 h-full w-full md:w-[560px] bg-white shadow-2xl z-[9999] rounded-l-2xl overflow-hidden transform transition-transform duration-300 ease-in-out flex flex-col ${isOpen ? "translate-x-0" : "translate-x-full"
                    }`}
            >
                <div className="relative overflow-hidden flex items-center justify-between px-6 py-5 border-b bg-green-50">
                    <div
                        className="absolute inset-0 bg-no-repeat bg-cover bg-center pointer-events-none"
                        style={{ backgroundImage: `url(${SidebarHeaderImage})` }}
                    />
                    <div className="absolute inset-0 bg-black/35 pointer-events-none" />
                    <div className="relative z-10 flex items-center justify-between w-full">
                        <div className="flex items-center gap-3">
                            <div className="bg-[#7f35b2]/30 p-2.5 rounded-xl shrink-0">
                                <UserCog className="text-white" size={22} />
                            </div>
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-100">Manage Responders</h2>
                                <p className="text-sm text-gray-100 mt-0.5">{survey.surveyName}</p>
                            </div>
                        </div>

                        <button
                            onClick={onClose}
                            className="p-2 rounded-lg hover:bg-gray-200/60 transition text-gray-100"
                        >
                            <X size={22} />
                        </button>
                    </div>
                </div>

                <div className="relative flex-1 overflow-hidden">
                    <div
                        className="absolute inset-0 bg-no-repeat bg-right-bottom opacity-[0.07] pointer-events-none"
                        style={{
                            backgroundImage: `url(${sidebarImage})`,
                            backgroundSize: "360px auto",
                        }}
                    />

                    <div className="relative h-full overflow-y-auto p-6 space-y-6">
                        <div className="flex border-b border-gray-200 mb-6">
                            <button
                                onClick={() => setActiveTab("bulk")}
                                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === "bulk"
                                    ? "border-[#7f35b2] text-[#7f35b2]"
                                    : "border-transparent text-gray-500 hover:text-gray-700"
                                    }`}
                            >
                                Bulk Upload
                            </button>
                            <button
                                onClick={() => setActiveTab("single")}
                                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === "single"
                                    ? "border-[#7f35b2] text-[#7f35b2]"
                                    : "border-transparent text-gray-500 hover:text-gray-700"
                                    }`}
                            >
                                Single Responder
                            </button>
                        </div>


                        {activeTab === "bulk" ? (
                            <>
                                <div className="bg-[#c2a8f01a] border border-[#c2a8f033] rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <div className="flex items-start gap-3">
                                        <div className="bg-white p-2 rounded-xl shadow-sm text-[#7f35b2] shrink-0 mt-0.5">
                                            <FileSpreadsheet size={20} />
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-semibold text-gray-900">Need a Responder Template?</h3>
                                            <p className="text-xs text-gray-500 mt-0.5">
                                                Download our sample Excel sheet to ensure your respondent emails and names are formatted correctly.
                                            </p>
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={handleDownloadSample}
                                        className="flex items-center justify-center gap-2 px-3.5 py-2 bg-white hover:bg-gray-50 text-gray-700 text-xs font-semibold rounded-xl border border-gray-200 shadow-sm transition shrink-0"
                                    >
                                        <Download size={14} className="text-[#7f35b2]" />
                                        Sample Sheet
                                    </button>
                                </div>

                                <div>
                                    <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
                                        <Upload size={14} className="text-[#7f35b2]" />
                                        Upload Responder Sheet
                                        <span className="text-red-500">*</span>
                                    </label>

                                    <label onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                        className={`border-2 border-dashed rounded-2xl p-8 flex flex-col items-center justify-center cursor-pointer transition ${isDragging
                                            ? "border-[#7f35b2] bg-[#7f35b2]/10 scale-[1.01]"
                                            : "border-gray-300 hover:border-[#7f35b2] hover:bg-[#7f35b2]/5"
                                            }`}>
                                        <Upload className="text-[#7f35b2] mb-3" size={36} />
                                        <p className="font-semibold text-gray-800 text-sm">Click or Drag & Drop to Upload</p>
                                        <p className="text-xs text-gray-400 mt-1">Supports .XLSX, .XLS, or .CSV</p>

                                        <input
                                            type="file"
                                            hidden
                                            accept=".xlsx,.xls,.csv"
                                            onChange={(e) => {
                                                setFile(e.target.files[0] ?? null);
                                                if (errors.file) setErrors((prev) => ({ ...prev, file: "" }));
                                            }}
                                        />
                                    </label>

                                    {errors.file && (
                                        <p className="flex items-center gap-1 text-xs text-red-600 mt-1.5">
                                            <AlertCircle size={12} />
                                            {errors.file}
                                        </p>
                                    )}

                                    {file && (
                                        <div className="mt-3 flex items-center justify-between gap-3 bg-green-50 border border-green-200 rounded-xl p-3">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <FileSpreadsheet size={18} className="text-green-600 shrink-0" />
                                                <div className="min-w-0">
                                                    <p className="text-sm font-medium text-gray-700 truncate">{file.name}</p>
                                                    <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                                                </div>
                                            </div>

                                            <button
                                                onClick={() => setFile(null)}
                                                className="p-1.5 rounded-lg hover:bg-green-100 text-gray-500 shrink-0 transition"
                                                title="Remove file"
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Responder Name <span className="text-red-500">*</span></label>
                                    <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7f35b2] focus:border-transparent"
                                        value={singleResponder.responderName}
                                        onChange={(e) => {
                                            setSingleResponder({ ...singleResponder, responderName: e.target.value });
                                            if (errors.responderName) setErrors((prev) => ({ ...prev, responderName: "" }));
                                        }}
                                    />
                                    {errors.responderName && <p className="text-xs text-red-600 mt-1">{errors.responderName}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Responder Email <span className="text-red-500">*</span></label>
                                    <input type="email" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7f35b2] focus:border-transparent"
                                        value={singleResponder.responderEmail}
                                        onChange={(e) => {
                                            setSingleResponder({ ...singleResponder, responderEmail: e.target.value });
                                            if (errors.responderEmail) setErrors((prev) => ({ ...prev, responderEmail: "" }));
                                        }}
                                    />
                                    {errors.responderEmail && <p className="text-xs text-red-600 mt-1">{errors.responderEmail}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                                    <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7f35b2] focus:border-transparent"
                                        value={singleResponder.phoneNumber}
                                        onChange={(e) => {
                                            setSingleResponder({ ...singleResponder, phoneNumber: e.target.value });
                                            if (errors.phoneNumber) setErrors((prev) => ({ ...prev, phoneNumber: "" }));
                                        }}
                                    />
                                    {errors.phoneNumber && <p className="text-xs text-red-600 mt-1">{errors.phoneNumber}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Location <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        rows={3}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7f35b2] focus:border-transparent resize-none"
                                        value={singleResponder.location}
                                        onChange={(e) => {
                                            setSingleResponder({ ...singleResponder, location: e.target.value });
                                            if (errors.location) setErrors((prev) => ({ ...prev, location: "" }));
                                        }}
                                    />
                                    {errors.location && <p className="text-xs text-red-600 mt-1">{errors.location}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Gender <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7f35b2] focus:border-transparent bg-white"
                                        value={singleResponder.gender}
                                        onChange={(e) => {
                                            setSingleResponder({ ...singleResponder, gender: e.target.value });
                                            if (errors.gender) setErrors((prev) => ({ ...prev, gender: "" }));
                                        }}
                                    >
                                        <option value="" disabled>Select gender</option>
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                        <option value="Others">Others</option>
                                    </select>
                                    {errors.gender && <p className="text-xs text-red-600 mt-1">{errors.gender}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Channel <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7f35b2] focus:border-transparent bg-white disabled:bg-gray-50 disabled:text-gray-400"
                                        value={singleResponder.channelID}
                                        disabled={loadingChannels}
                                        onChange={(e) => {
                                            setSingleResponder({ ...singleResponder, channelID: e.target.value });
                                            if (errors.channelID) setErrors((prev) => ({ ...prev, channelID: "" }));
                                        }}
                                    >
                                        <option value="" disabled>
                                            {loadingChannels ? "Loading channels..." : "Select a channel"}
                                        </option>
                                        {channels
                                            .filter((ch) => ch.isactive)
                                            .map((ch) => (
                                                <option key={ch.id} value={ch.id}>
                                                    {ch.value}
                                                </option>
                                            ))}
                                    </select>
                                    {errors.channelID && <p className="text-xs text-red-600 mt-1">{errors.channelID}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Experience Type <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7f35b2] focus:border-transparent bg-white"
                                        value={singleResponder.experienceType}
                                        onChange={(e) => {
                                            setSingleResponder({ ...singleResponder, experienceType: e.target.value });
                                            if (errors.experienceType) setErrors((prev) => ({ ...prev, experienceType: "" }));
                                        }}
                                    >
                                        <option value="" disabled>Select experience type</option>
                                        {EXPERIENCE_TYPE_OPTIONS.map((type) => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </select>
                                    {errors.experienceType && <p className="text-xs text-red-600 mt-1">{errors.experienceType}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Reference Code / ID</label>
                                    <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7f35b2] focus:border-transparent"
                                        value={singleResponder.referenceCode}
                                        onChange={(e) => setSingleResponder({ ...singleResponder, referenceCode: e.target.value })}
                                    />
                                </div>
                            </div>
                        )}


                        <div className="bg-[#c2a8f01a] border border-[#c2a8f033] rounded-2xl p-4">
                            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
                                <Calendar size={14} className="text-[#7f35b2]" />
                                Deadline <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="datetime-local"
                                min={new Date().toISOString().slice(0, 16)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#7f35b2] focus:border-transparent"
                                value={deadline}
                                onChange={(e) => {
                                    setDeadline(e.target.value);
                                    if (errors.deadline) setErrors((prev) => ({ ...prev, deadline: "" }));
                                }}
                            />
                            {errors.deadline && (
                                <p className="flex items-center gap-1 text-xs text-red-600 mt-1.5">
                                    <AlertCircle size={12} />
                                    {errors.deadline}
                                </p>
                            )}
                            {activeTab === "bulk" && (
                                <div className="mt-3 flex items-center gap-2 text-xs text-gray-600 bg-white/70 border border-[#c2a8f033] rounded-xl px-3 py-2">
                                    <Calendar size={13} className="text-[#7f35b2] shrink-0" />
                                    <span>
                                        Next bulk upload run:{" "}
                                        <span className="font-semibold text-gray-800">
                                            {nextRunTime.toLocaleTimeString("en-IN", {
                                                timeZone: "Asia/Kolkata",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                                hour12: true,
                                            })}
                                        </span>{" "}
                                       
                                    </span>
                                </div>
                            )}

                        </div>
                    </div>
                </div>

                <div className="border-t bg-white px-6 py-4 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        disabled={submitting}
                        className="px-5 py-2.5 border border-gray-300 rounded-xl hover:bg-gray-50 text-sm font-medium transition disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="flex items-center gap-2 px-5 py-2.5 bg-[#7f35b2] hover:bg-[#3bb290] text-white rounded-xl text-sm font-medium transition shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        <CheckCircle2 size={16} />
                        {submitting ? "Saving..." : "Save Responders"}
                    </button>
                </div>
            </div>
        </>
    );
};

export default PublishResponderModel;